import React, {Component} from 'react';
import {Flex, Select} from '@priority-software/priority-style-react';
import {ColumnCode} from 'priority-web-sdk/enums';
import moment from 'moment';
import 'moment/locale/en-gb';

import DatePicker from './DatePicker';
import {Operator} from '../../consts/operators';
import {convertToFnsFormat, getLocale} from '../../../../Localization';
import {openDateSearchField} from '../../actions/filters';
import {getLocalizedText, texts} from '../../../../constants/TextConst';
import {isSpecialDate} from '../../../library/utils/date';
import {Label} from '../../../../components/Common/Label';
import {format} from 'date-fns';

type DateValue = {min: string; max: string} | undefined;

enum DateType {
    start = 'start',
    end = 'end'
}

interface DateControlProps {
    value: DateValue;
    format: string;
    operator: Operator;
    code: ColumnCode;
    onChange(value: DateValue): void;
    setActive(): void;
    unsetActive(): void;
    autoFocus: boolean;
    isDisabled: boolean;
}

interface DateOption {
    value?: string;
    label: string;
    date?: string;
    idate?: string;
}

interface DateControlState {
    startDate: string;
    endDate: string;
    dateRange: DateOption[];
}

export class DateControl extends Component<DateControlProps, DateControlState> {
    local = getLocale();
    customOption = {label: getLocalizedText(texts.customRange)};
    state = {
        startDate: null,
        endDate: null,
        dateRange: []
    };

    componentDidMount() {
        const {format} = this.props;

        openDateSearchField(format)
            .then(res => {
                this.setState({dateRange: res});
                this.initState();
            })
            .catch(err => console.log(err));
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {value} = nextProps;

        //clear the field in case of not receiving values
        !value && this.clearValues();
    }

    initState = () => {
        const {value} = this.props;

        if (value) {
            const {min, max} = value;
            this.setState({startDate: min, endDate: max});
        }
    };

    getFormatedDate = date => {
        /**
         * In case the user has entered 12/03 for the purpose of registering 12/03.
         * The date 12/0 will not pass the moment.js validation (ie isValid()).
         * Changing the number from zero to one allows you to run a test.
         */
        // if(isValid(date,this.props.format) || isSpecialDate(date)){
        //     return date;
        // }
        //if option
        if (moment(date, this.props.format).isValid() || isSpecialDate(date)) {
            return date;
        } else {
            const option =
                this.state.dateRange && this.state.dateRange.find(option => option.label === date);
            return option ? option.date : date;
        }
    };

    getOption = date => {
        let option;

        if (date) {
            option =
                this.state.dateRange && this.state.dateRange.find(option => option.label === date);
            if (!option) return this.customOption;
        }

        return option;
    };

    clearValues = () => {
        this.setState({
            startDate: null,
            endDate: null
        });
    };
    updateDate = (startDate, endDate) => {
        this.props.onChange({min: startDate, max: endDate});
        this.setState({startDate: startDate, endDate: endDate});
    };

    handleOnChangeDate = (key: DateType, date: Date | string) => {
        const {value} = this.props;

        const dateString =
            !date || typeof date === 'string'
                ? date
                : format(date, convertToFnsFormat(this.props.format));

        const start = key === DateType.start ? dateString : value && value.min;
        const end = key === DateType.end ? dateString : value && value.max;

        this.updateDate(start, end);
    };

    handleOnChangeOption = (key: DateType, option, {action}) => {
        if (action === 'select-option') {
            const {value} = this.props;

            const start = key === DateType.start ? option.value : value && value.min;
            const end = key === DateType.end ? option.value : value && value.max;

            this.updateDate(start, end);
        }
    };

    renderDate = (key: DateType) => {
        const {operator, format, code} = this.props;
        const option = this.getOption(this.state[`${key}Date`]);
        const date = this.getFormatedDate(this.state[`${key}Date`]);
        const text =
            key === DateType.start ? getLocalizedText(texts.from) : getLocalizedText(texts.to);
        const isShowTime = code === ColumnCode.Date14;
        const datePickerClass = `custom ignore-react-onclickoutside ${
            date && date.toDate && date.toDate().getDate() > 9 && `long-${key}-date`
        }`;

        return (
            <Flex componentClasses="control-row">
                {operator === Operator.Between && (
                    <Label componentClasses="date-control-label">{text}</Label>
                )}
                <Select
                    /* we should change the key to force the component to re-render and clear internal state
                     when the operator changes.
                     In another case, we still see the prev selected value that was cleared from the filter.
                     */
                    key={`${operator}-key`}
                    options={this.state.dateRange}
                    onChange={(value: any, data: any) =>
                        this.handleOnChangeOption(key, value, data)
                    }
                    isSearchable={false}
                    placeholder={null}
                    componentClasses="date-range"
                    selectMenuClasses="ignore-react-onclickoutside"
                    onFocus={this.props.setActive}
                    onBlur={this.props.unsetActive}
                    onMenuOpen={this.props.setActive}
                    onMenuClose={this.props.unsetActive}
                    value={option}
                    menuScrollMaxHeight={240}
                    isDisabled={this.props.isDisabled}
                />
                <div className="date-picker">
                    <DatePicker
                        disabled={this.props.isDisabled}
                        locale={this.local}
                        dateFormat={format}
                        selected={date}
                        selectsStart={key === DateType.start}
                        selectsEnd={key === DateType.end}
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        onChange={date => this.handleOnChangeDate(key, date)}
                        calendarClassName={datePickerClass}
                        showTimeSelect={isShowTime}
                        timeIntervals={15}
                        timeFormat={format.slice(9)}
                    />
                </div>
            </Flex>
        );
    };

    render() {
        const {operator} = this.props;

        const showFrom = operator !== Operator.Before;
        const showTo = operator === Operator.Between || operator === Operator.Before;

        return (
            <div className="date-control">
                {showFrom && this.renderDate(DateType.start)}
                {showTo && this.renderDate(DateType.end)}
            </div>
        );
    }
}
